import Handlebars from 'handlebars';
import { Trans, useTranslation } from 'react-i18next';
import { formatPhoneNumber } from 'react-phone-number-input';
import Markdown from 'shared/components/Markdown';
import { SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import Spacer from 'design-system/components/Spacer';
import ImageMobileSms from 'images/pictographs/mobile-sms.svg';
import Button, { ButtonType } from 'inquiry/components/Button';
import CustomAssetWithFallback from 'inquiry/components/CustomAssetWithFallback';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import { useBaseStepStyleContext } from 'inquiry/context/BaseStepStyleContext';
import useToggle from 'lib/hooks/useToggle';
import { colorCss } from 'lib/inquiryStyles';
import type {
  DeviceHandoffAssets,
  DeviceHandoffSmsSentPageLocalizations,
} from 'lib/interfaces/StepsConfig';
import CenteredPictograph from 'verify/components/CenteredPictograph';
import ThemedSvg from 'verify/components/ThemedSvg';
import { MIN_HEIGHTS } from 'verify/styles/variables';

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${SPACINGS.base * 4}px;
`;

interface InquiryDeviceHandoffSmsSentViewProps {
  assets: DeviceHandoffAssets['device-handoff-sms-sent-page'];
  localizations?: DeviceHandoffSmsSentPageLocalizations;
  phoneNumber: string;
  resendOneTimeLink: () => void;
}

export default function InquiryDeviceHandoffSmsSentView({
  assets,
  localizations,
  phoneNumber,
  resendOneTimeLink,
}: InquiryDeviceHandoffSmsSentViewProps) {
  const formattedPhoneNumber = formatPhoneNumber(phoneNumber, 'National');

  const [t] = useTranslation();
  const { base, buttons } = useBaseStepStyleContext();
  const [linkSent, { enable: setLinkSent }] = useToggle();

  const handleResendOneTimeLink = () => {
    if (linkSent) {
      // Don't allow spam-resends
      return;
    }
    resendOneTimeLink();
    setLinkSent();
  };

  return (
    <>
      <CustomAssetWithFallback customImage={assets['header-pictograph']} alt='header pictograph' />
      <Header>
        <ThemedTitle styles={base?.titleStyle}>
          <Markdown>{localizations?.title ?? t('mobile-sent.title')}</Markdown>
        </ThemedTitle>
        <ThemedParagraph styles={base?.textStyle}>
          {localizations?.prompt ? (
            <Markdown>
              {Handlebars.compile<{ client: { phoneNumber: string } }>(localizations.prompt, {
                data: false,
                knownHelpersOnly: true,
              })({ client: { phoneNumber: formattedPhoneNumber } })}
            </Markdown>
          ) : (
            <Trans
              i18nKey='mobile-sent.prompt'
              components={[<bdo dir='ltr' key={0} />]}
              values={{ phoneNumber: formattedPhoneNumber }}
            />
          )}
        </ThemedParagraph>
      </Header>
      <CenteredPictograph aria-label='SMS pictograph'>
        <CustomAssetWithFallback customImage={assets['sms-pictograph']} alt='SMS pictograph'>
          <ThemedSvg
            $primaryColor={colorCss(base?.fill?.color)}
            $highlightColor={colorCss(base?.fill?.highlightColor)}
            $backgroundColor={colorCss(base?.fill?.backgroundColor)}
            $strokeColor={colorCss(base?.stroke?.color)}
          >
            <ImageMobileSms />
          </ThemedSvg>
        </CustomAssetWithFallback>
      </CenteredPictograph>
      {localizations?.['btn-resend'] ? (
        <>
          <Spacer height={2} />
          <Button
            id='inquiry-device-handoff-sms-sent__resend-btn'
            onClick={handleResendOneTimeLink}
            type='button'
            buttonType={ButtonType.Secondary}
            styles={buttons?.[ButtonType.Secondary]}
          >
            {localizations['btn-resend']}
          </Button>
        </>
      ) : (
        <ThemedParagraph styles={base?.textStyle}>
          <Trans i18nKey='mobile-sent.resend-link'>
            Click here to
            <a id='device-handoff-sms__resend-link' onClick={handleResendOneTimeLink}>
              resend the secure link
            </a>
          </Trans>
        </ThemedParagraph>
      )}
    </>
  );
}
